import dagre from 'dagre';
import { nodeWidth } from './nodeTypes';

/* Run dagre to lay out a node graph top-to-bottom. Returns a new array
   of nodes with their positions updated. Node sizes are estimated from
   the number of output handles so menu nodes don't overlap. */
export function tidyLayout(nodes, edges) {
  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  // Top-to-bottom layout with generous spacing so the new bottom-edge
  // output handles + edge labels don't collide. nodesep controls
  // horizontal gap between siblings; ranksep is the vertical gap
  // between layers.
  g.setGraph({ rankdir: 'TB', nodesep: 70, ranksep: 110, marginx: 32, marginy: 32 });

  nodes.forEach((n) => {
    const w = nodeWidth(n.type, n.data);
    const h = 130;
    g.setNode(n.id, { width: w, height: h });
  });
  edges.forEach((e) => g.setEdge(e.source, e.target));
  dagre.layout(g);

  return nodes.map((n) => {
    const pos = g.node(n.id);
    return { ...n, position: { x: pos.x - pos.width / 2, y: pos.y - pos.height / 2 } };
  });
}

/* Convert a legacy schedule-based flow into a starter node graph so the
   user has something visible to drag around. Each schedule becomes a
   Time Router → AI Agent path; the fallback becomes the "closed" branch.
   This runs ONCE on first canvas open if the flow has no nodes yet. */
export function legacyToNodes(flow) {
  const nodes = [];
  const edges = [];
  const startId = 'n_start';
  nodes.push({
    id: startId, type: 'start', position: { x: 0, y: 0 },
    data: { label: flow.name || 'Inbound', greeting: '' },
  });

  const schedules = flow.schedules || [];
  let prevSrc = startId;
  let prevHandle = 'out';

  if (schedules.length > 0) {
    const first = schedules[0];
    const trId = 'n_time';
    nodes.push({
      id: trId, type: 'time_router', position: { x: 0, y: 0 },
      data: {
        timezone: 'America/New_York',
        windows: [{ days: first.days || ['mon','tue','wed','thu','fri'],
                    start: first.start_time || '09:00',
                    end:   first.end_time || '17:00' }],
      },
    });
    edges.push({ id: `e_${prevSrc}_${trId}`, source: prevSrc, target: trId, sourceHandle: prevHandle, type: 'smoothstep', animated: true });

    // Open hours → first target (AI agent or transfer)
    const tgt = (first.targets || [])[0];
    if (tgt) {
      const isAgent = tgt.type === 'ai_agent';
      const aId = 'n_open';
      nodes.push({
        id: aId, type: isAgent ? 'ai_agent' : 'transfer', position: { x: 0, y: 0 },
        data: isAgent ? { agent_id: tgt.agent_id, agent_name: tgt.agent_name } : { number: tgt.number, label: tgt.label },
      });
      edges.push({ id: `e_${trId}_${aId}_open`, source: trId, target: aId, sourceHandle: 'open', type: 'smoothstep', animated: true });
    }

    // Closed → fallback
    const fb = flow.fallback;
    if (fb && fb.agent_id) {
      const fId = 'n_closed';
      nodes.push({
        id: fId, type: 'ai_agent', position: { x: 0, y: 0 },
        data: { agent_id: fb.agent_id, agent_name: fb.agent_name },
      });
      edges.push({ id: `e_${trId}_${fId}_closed`, source: trId, target: fId, sourceHandle: 'closed', type: 'smoothstep', animated: true });
    }
  }

  return { nodes: tidyLayout(nodes, edges), edges };
}
